import latinize from 'latinize'
import {useParams} from 'next/navigation'
import {FC, useEffect, useState} from 'react'
import Highlighter from 'react-highlight-words'

import {CollapsibleSubmenu} from '@/components/header/submenu/CollapsibleSubmenu'
import {SubmenuItem} from '@/components/header/submenu/SubmenuItem'
import {Link} from '@/i18n'
import {theme} from '@/theme/theme'
import {routeTo} from '@/utils/router'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../../common/content/Text'
import {ChevronIcon} from '../../common/icons/ChevronIcon'

type SidebarCategoryProps = {
  title: string
  articles: Array<{
    slug: string
    title: string
    order: number
  }>
  searchWords?: string[]
  onClick?: () => void
  // Renders the mobile navigation's variant. Each sidebar is only ever shown at one breakpoint, so it
  // renders one variant rather than both behind a media query, which duplicated every link.
  isMobile?: boolean
}

// The links don't prefetch: an article's prefetch is its whole static page (~130 kB), once per link
// in view.
export const SidebarCategory: FC<SidebarCategoryProps> = ({
  title,
  articles,
  searchWords,
  onClick,
  isMobile = false,
}) => {
  const textColor = isMobile ? theme.palette.text.white : theme.palette.text.default
  const querySlug = useParams<{slug?: string}>()?.slug
  const hasCurrentArticle = articles.some(({slug}) => slug === querySlug)
  // Initialised, not only set in the effect, so the prerendered sidebar opens the current category.
  const [isExpanded, setIsExpanded] = useState(hasCurrentArticle)

  useEffect(() => {
    setIsExpanded(hasCurrentArticle)
  }, [hasCurrentArticle, querySlug])

  return (
    <>
      <div className="item">
        <button
          className="title"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          type="button"
        >
          <ChevronIcon
            variant={isMobile ? 'down' : 'right'}
            isActive={isExpanded}
            color={textColor}
          />
          <Text color={textColor} fontWeight={TextFontWeight.bold} align="left">
            {searchWords ? (
              <Highlighter
                searchWords={searchWords}
                sanitize={latinize}
                autoEscape
                textToHighlight={title}
                highlightClassName="highlighted"
              />
            ) : (
              title
            )}
          </Text>
        </button>
        <CollapsibleSubmenu isActive={isExpanded} keepMounted>
          <ul className="articles">
            {articles?.map(({slug, title}) => {
              const isActive = querySlug === slug

              return (
                <li
                  key={`article-${slug}`}
                  className="article"
                  onClick={onClick}
                  role="presentation"
                >
                  {isMobile ? (
                    <SubmenuItem
                      title={{text: title, textVariant: TextVariant.text14}}
                      linkHref={routeTo.tmAcademy.article(slug)}
                      prefetch={false}
                      isBulletPoint
                      textColor={isActive ? theme.palette.primary.main : textColor}
                    />
                  ) : (
                    <Text
                      variant={TextVariant.text14}
                      color={isActive ? theme.palette.primary.main : textColor}
                    >
                      <Link href={routeTo.tmAcademy.article(slug)} prefetch={false}>
                        {title}
                      </Link>
                    </Text>
                  )}
                </li>
              )
            })}
          </ul>
        </CollapsibleSubmenu>
      </div>

      <style jsx>{`
        .title {
          display: flex;
          align-items: center;
          gap: ${theme.spacing[2]};
        }

        .articles {
          margin-top: ${theme.spacing[3]};
          margin-left: ${theme.spacing[4.5]};
        }

        .article + .article {
          margin-top: ${theme.spacing[3]};
        }

        @media ${TABLET_SCREENSIZE} {
          .title {
            width: 100%;
            justify-content: space-between;
            flex-direction: row-reverse;
            padding: ${theme.spacing[3]} 0;
          }

          .articles {
            margin-top: 0;
            margin-left: ${theme.spacing[2]};
          }

          .article + .article {
            margin-top: ${theme.spacing[3]};
          }

          .article:last-of-type {
            padding-bottom: ${theme.spacing[3.5]};
          }
        }
      `}</style>

      <style jsx global>{`
        .highlighted {
          background-color: ${theme.palette.primary.main};
        }
      `}</style>
    </>
  )
}
